import { all, tryFirst } from './util';

class Carousel {

    private readonly carousel: HTMLDivElement
    private readonly images: Array<HTMLImageElement>
    private readonly leftArrow: SVGElement | null
    private readonly rightArrow: SVGElement | null

    private currentImageIndex: number
    private currentlyFadingOutRight: boolean
    private currentlyFadingOutLeft: boolean
    private canClick: boolean
    private swipeDone: boolean
    private initialX: number

    constructor(carouselContainer: HTMLDivElement) {
        this.carousel = carouselContainer;
        this.currentImageIndex = 0;
        this.currentlyFadingOutRight = false;
        this.currentlyFadingOutLeft = false;
        this.canClick = true;
        this.initialX = 0;
        this.swipeDone = false;

        this.leftArrow = tryFirst<SVGElement>('.left', carouselContainer);
        this.rightArrow = tryFirst<SVGElement>('.right', carouselContainer);
        this.images = all<HTMLImageElement>('.carousel__image', carouselContainer);

        this.initArrows();
        this.initSwipe();
        this.initTouchStart();
        this.initTouchEnd();
    }

    nextImage(assignIndex: boolean) {
        const originalIndex = this.currentImageIndex;
        if (this.currentImageIndex == 0) {
            this.currentImageIndex = this.images.length - 1;
        } else {
            this.currentImageIndex = this.currentImageIndex - 1;
        }

        const img = this.images[this.currentImageIndex];
        this.currentImageIndex = !assignIndex ? originalIndex : this.currentImageIndex;
        return img;
    }

    prevImage(assignIndex: boolean) {
        const originalIndex = this.currentImageIndex;

        if (this.currentImageIndex == this.images.length - 1) {
            this.currentImageIndex = 0;
        } else {
            this.currentImageIndex = this.currentImageIndex + 1;
        }

        const img = this.images[this.currentImageIndex];
        this.currentImageIndex = !assignIndex ? originalIndex : this.currentImageIndex;
        return img;
    }

    currentImage() {
        const currentImages = Array.from(this.carousel.getElementsByClassName('current'));
        if (currentImages.length) {
            return currentImages[0];
        } else {
            throw new Error('no current image exists..?');
        }
    }

    clearFade(clearLeft: boolean) {
        const className =
            clearLeft
                ? 'carousel__image--to-left'
                : 'carousel__image--to-right'

        all('.carousel__image').forEach(i => i.classList.remove(className));
    }

    clearFadeLeft() {
        this.clearFade(true);
    }

    clearFadeRight() {
        this.clearFade(false);
    }

    fadeOut(toLeft: boolean) {
        const fadeOut = toLeft ? this.prevImage(false) : this.nextImage(false);
        const fadeOutClassName = toLeft ? 'carousel__image--to-left' : 'carousel__image--to-right';
        fadeOut.classList.add(fadeOutClassName);

        if (toLeft) {
            this.currentlyFadingOutLeft = true;
            if (this.currentlyFadingOutRight) {
                this.clearFadeRight();
            }
        } else {
            this.currentlyFadingOutRight = true;
            if (this.currentlyFadingOutLeft) {
                this.clearFadeLeft();
            }
        }

        setTimeout(() => {
            if (toLeft) {
                this.currentlyFadingOutLeft = false;
            } else {
                this.currentlyFadingOutRight = false;
            }
            fadeOut.classList.remove(fadeOutClassName);
        }, 998);
    }

    fadeOutRight() {
        this.fadeOut(false)
    }

    fadeOutLeft() {
        this.fadeOut(true);
    }

    arrowClickEvent = (swipeLeft: boolean) => {
        if (this.canClick) {
            const currentImage = this.currentImage();
            const nextImage = swipeLeft ? this.nextImage(true) : this.prevImage(true);

            currentImage.classList.remove('current');
            nextImage.classList.add('current');
            this.fadeOutLeft();

            if (swipeLeft) {
                this.fadeOutLeft();
            } else {
                this.fadeOutRight();
            }

            this.canClick = false;
            setTimeout(() => {
                this.canClick = true;
            }, 300);
        }
    }

    rightArrowClickEvent = () => {
        this.arrowClickEvent(false);
    }

    leftArrowClickEvent = () => {
        this.arrowClickEvent(true);
    }

    initArrows() {
        if (this.rightArrow) {
            this.rightArrow.addEventListener('click', this.rightArrowClickEvent);
        }
        if (this.leftArrow) {
            this.leftArrow.addEventListener('click', this.leftArrowClickEvent);
        }
    }

    swipeEventHandler = (e: TouchEvent) => {
        if (e.touches && e.touches.length) {
            if (!this.swipeDone) {
                const touch = e.touches[0];
                const distance = touch.clientX - this.initialX;

                if (distance > 30) {
                    this.rightArrowClickEvent();
                    this.swipeDone = true;
                } else if (distance < -30) {
                    this.swipeDone = true;
                    this.leftArrowClickEvent();
                }
            }
        }
    }

    initSwipe() {
        this.carousel.addEventListener('touchmove', this.swipeEventHandler);
    }

    touchStartEventHandler = (e: TouchEvent) => {
        if (e.touches && e.touches.length) {
            this.initialX = e.touches[0].clientX;
            document.body.classList.add('prevent-scroll-on-body');
            setTimeout(() => {
                document.body.classList.remove('prevent-scroll-on-body')
            }, 100);
        }
    }

    initTouchStart() {
        this.carousel.addEventListener('touchstart', this.touchStartEventHandler);
    }

    touchEndEventHandler = () => {
        this.swipeDone = false;
    }

    initTouchEnd() {
        this.carousel.addEventListener('touchend', this.touchEndEventHandler);
    }
}

(() => all<HTMLDivElement>('.carousel').forEach(c => new Carousel(c)))();